/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:37:54                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:34:24                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * ContactSelect: contact select component
    * useEffect: reset value
    * formatValue: format value before display in select
    * useEffect: convert value to comparable with select data
    * getContactList: Get contacts from API
    * contactChange: handle contact change event
    * handleContactCreate: handle contact creation event
*/

/* Imports */
import AsyncSelect from "react-select/async";
import { useEffect, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../../services/api/error";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
/***/

/* Components */
import EditCreateDrawer from "../../edit-create-drawer/EditCreateDrawer";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
import ContactForm from "./ContactForm";
/***/

/**
* ContactSelect: contact select component
* Used to select a contact entity, it can open the 
* contact edit component to create or edit a contact.
* The customer props is always REQUIRED
* @param value - current value
* @param customerId - parent id
* @param onChange - on value change event
* @param disabled - enabled/disbaled state
* @param isClearable - allow clear field feature
* @param isMulti - allow multi selection feature
* @param isCreatable - allow creation on fly
* @param isEditable - allow creation on fly
* @param className - additional classNames
*/
export default function ContactSelect({
  value,
  customerId,
  onChange,
  disabled,
  isClearable,
  isMulti = false,
  isCreatable,
  isEditable,
  className = ""
}: {
  value?: EasyTech.Contact | EasyTech.Contact[],
  customerId: string,
  onChange?: (value: EasyTech.Contact | EasyTech.Contact[] | undefined) => void,
  disabled?: boolean
  isClearable?: boolean,
  isMulti?: boolean,
  isCreatable?: boolean,
  isEditable?: boolean,
  className?: string
}) {
  const [valueState, setValueState] = useState<EasyTech.IFormData<EasyTech.Contact> | EasyTech.IFormData<EasyTech.Contact>[] | null>();
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  const [newContact, setNewContact] = useState<any>();

  
  /**
  * useEffect: convert value to comparable with select data
  * @param value - reload on value change 
  */
  useEffect(() => {
    if (value) {
      if (isMulti) {
        const contact = value as EasyTech.Contact[];
        const arr = setSelectList<EasyTech.Contact>(contact , (el) => (`${el.firstName} ${el.lastName}`));
        setValueState(arr as EasyTech.IFormData<EasyTech.Contact>[]);
      } else {
        const tmp = {...value} as EasyTech.Contact;
        setValueState({value: value, label: `${tmp.firstName} ${tmp.lastName}`} as EasyTech.IFormData<EasyTech.Contact>);
      }
    } else setValueState(null);
  }, [value, isMulti]);
  /***/

  /**
  * getContactList: Get contacts from API
  * @param inputValue - search input value for filtering contacts
  * @return - contact list 
  */
  const getContactList = (inputValue = ""): Promise<EasyTech.IFormData<EasyTech.Contact>[]> => {
    return new Promise(resolve => {
      api.get(`users/customers/${customerId}/contacts`).then(ret => {
        const tmp = ret as EasyTech.Contact[];
  
        if (newContact) {
          const c = tmp.find(el => el.id === newContact.id);
          if (c && onChange) onChange(c);
          setNewContact(null);
        }

        // Filter contacts based on search input
        const filteredContacts = tmp.filter(contact => {
          if (!inputValue) return true; // Show all if no search input
          
          const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
          const searchTerm = inputValue.toLowerCase();
          
          return fullName.includes(searchTerm) || 
                 contact.firstName.toLowerCase().includes(searchTerm) ||
                 contact.lastName.toLowerCase().includes(searchTerm) ||
                 (contact.email && contact.email.toLowerCase().includes(searchTerm)) ||
                 (contact.phone && contact.phone.includes(searchTerm));
        });

        return resolve(
          setSelectList<EasyTech.Contact>(filteredContacts, (el) => (`${el.firstName} ${el.lastName}`))
        );
      });
      
    });
  };
  /***/

  /**
  * contactChange: handle contact change event
  * @param newValue - New select value
  */
  const contactChange = (
    newValue: MultiValue<EasyTech.IFormData<EasyTech.Contact>> | SingleValue<EasyTech.IFormData<EasyTech.Contact>>
  ) => {
    let ret: EasyTech.Contact | EasyTech.Contact[];

    if (isMulti) {
      const tmp = newValue as MultiValue<EasyTech.IFormData<EasyTech.Contact>>;
      ret = tmp.map((el) => el.value);
    } else {
      const tmp = newValue as SingleValue<EasyTech.IFormData<EasyTech.Contact>>;
      ret = tmp ? tmp.value : undefined as unknown as EasyTech.Contact;
    }

    if (onChange) onChange(ret);
  };
  /***/

  /**
  * handleContactCreate: handle contact creation event
  * @param data - new contact data 
  */
  const handleContactCreate = (data: EasyTech.Contact): Promise<void> => {
    return new Promise((resolve, reject) => {
      api.post("users/contacts", {...data, customer: {id: customerId}})
        .then((ret) => {
          toast.success("Contact created !");
          setTimestamp(Date.now()); // Force list reload
          setNewContact(ret);
          return resolve();
        }).catch((err) => {
          console.error(err);
          showApiErrorToast(err, { fallbackKey: "contact:createError" });
          return reject();
        });
    });
  };
  /***/

  const SelectField = isMulti ? MultiCheckSelect : AsyncSelect;

  return (
    <div className={`flex w-full min-w-0 items-center gap-2 ${className}`}>
      <SelectField  key={`${customerId} ${timestamp}`}
                    isMulti={isMulti}
                    cacheOptions={false}
                    defaultOptions={true}
                    loadOptions={getContactList}
                    value={valueState}
                    theme={reactSelectTheme}
                    onChange={contactChange}
                    className="min-w-0 flex-1"
                    name="contact"
                    placeholder="Search or select a Contact"
                    classNamePrefix="react-select"
                    isClearable={isClearable}
                    isDisabled={disabled}
                    closeMenuOnSelect={!isMulti}
                    hideSelectedOptions={!isMulti}
                    noOptionsMessage={({ inputValue }: { inputValue: string }) => 
                      inputValue ? `No contacts found for "${inputValue}"` : "No contacts available"
                    }
                    loadingMessage={() => "Searching contacts..."}
                    filterOption={null} // Disable client-side filtering since we filter server-side
                    />

      <EditCreateDrawer<EasyTech.Contact>   FormComponent={ContactForm}
                                            isNotEditable={!isEditable}
                                            isNotCreatable={!isCreatable}
                                            onSubmit={handleContactCreate}
                                            entityName="contact"/>
    </div>
  );
}
/***/
